import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Pencil, Plus, Trash2, UserRoundCog } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "@doculegis/shared";
import { GlassCard } from "@/components/common/GlassCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PageMobileActions } from "@/components/common/PageMobileActions";
import {
  DraftLegalRoleSheet,
  type DraftLegalRoleSheetDraft,
} from "@/components/draft-legal-roles/DraftLegalRoleSheet";
import { Button } from "@/components/ui/button";
import { deleteDraftLegalRole, fetchDraftLegalRoles } from "@/lib/api/drafts";
import {
  pageActionButtonClassName,
  pageActionDesktopOnlyClassName,
  pageActionLabelClassName,
} from "@/lib/page-action-button";
import { usePermission } from "@/contexts/AuthContext";
import type { DraftLegalRole } from "@/types/drafting";
import { InlineAssistantLayout } from "@/components/assistant/InlineAssistantLayout";
import { DraftLegalRoleAssistantPanel } from "../components/draft-legal-role-assistant/DraftLegalRoleAssistantPanel";

export function DraftLegalRolesPage() {
  const canCreate = usePermission("draftLegalRole", "create");
  const canUpdate = usePermission("draftLegalRole", "update");
  const showAssistant = canCreate || canUpdate;
  const canDelete = usePermission("draftLegalRole", "delete");
  const canDeleteSystem = usePermission("draftLegalRole", "deleteSystem");

  const [roles, setRoles] = useState<DraftLegalRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<DraftLegalRoleSheetDraft | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DraftLegalRole | null>(null);
  const [removing, setRemoving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DraftLegalRole | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [highlightRoleId, setHighlightRoleId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const editableRows = useMemo(() => roles.filter((role) => !role.is_system), [roles]);
  const systemRows = useMemo(() => roles.filter((role) => role.is_system), [roles]);

  const loadRoles = useCallback(async (options?: { background?: boolean }) => {
    const background = options?.background === true;
    if (!background) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const rows = await fetchDraftLegalRoles();
      setRoles(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load legal roles");
    } finally {
      if (!background) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (!highlightRoleId) return;
    const timer = window.setTimeout(() => setHighlightRoleId(null), 3000);
    return () => window.clearTimeout(timer);
  }, [highlightRoleId]);

  function openCreate() {
    setSheetDraft({
      mode: "create",
      name: "",
      role_key: "",
      instructions: "",
    });
    setSheetOpen(true);
  }

  function openCustomize(item: DraftLegalRole) {
    setSheetDraft({
      mode: "customize",
      name: item.name,
      role_key: item.role_key,
      instructions: item.instructions,
    });
    setSheetOpen(true);
  }

  function openEdit(item: DraftLegalRole) {
    setSheetDraft({
      mode: "edit",
      id: item.id,
      name: item.name,
      role_key: item.role_key,
      instructions: item.instructions,
    });
    setSheetOpen(true);
  }

  async function handleRemoveFromOrg() {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await deleteDraftLegalRole(removeTarget.id);
      setRemoveTarget(null);
      await loadRoles();
    } finally {
      setRemoving(false);
    }
  }

  async function handleDeleteCustom() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDraftLegalRole(deleteTarget.id);
      setDeleteTarget(null);
      await loadRoles();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section
      className={cn(
        "relative p-6 md:p-8",
        assistantOpen && "flex min-h-0 flex-1 flex-col overflow-hidden"
      )}
    >
      <div className="mb-5 flex shrink-0 flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] text-[#7c3aed] dark:border-violet-300/30 dark:bg-violet-500/15 dark:text-violet-200">
            <UserRoundCog className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Draft Legal Roles
            </h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Manage system defaults and your customized drafting counsel perspectives.
            </p>
          </div>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={openCreate}
            className={`${pageActionButtonClassName} ${pageActionDesktopOnlyClassName}`}
          >
            <Plus className="h-4 w-4" />
            <span className={pageActionLabelClassName}>New role</span>
          </Button>
        ) : null}
      </div>

      {canCreate ? (
        <PageMobileActions
          actions={[{ id: "new-role", icon: Plus, label: "New role", onClick: openCreate }]}
        />
      ) : null}

      <InlineAssistantLayout
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        showTrigger={showAssistant}
        triggerLabel="Draft Legal Role Assistant"
        assistant={
          <DraftLegalRoleAssistantPanel
            className="h-full min-h-0"
            roles={roles}
            onRefresh={loadRoles}
            onClose={() => setAssistantOpen(false)}
            onDraftLegalRoleWritten={setHighlightRoleId}
          />
        }
      >
      <GlassCard className="overflow-hidden border-[var(--lm-border-card)] bg-white p-0 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none">
        <h2 className="border-b border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-3 text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:border-white/10 dark:bg-transparent dark:text-slate-400">
          All legal roles
        </h2>
        {isLoading ? <p className="px-4 py-3.5 text-sm text-[var(--lm-muted)]">Loading...</p> : null}
        {error ? <p className="px-4 py-3.5 text-sm text-red-300">{error}</p> : null}

        <div className="divide-y divide-[var(--lm-border-subtle)] dark:divide-white/10">
          {systemRows.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3.5">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">
                  {item.name}
                </p>
                <span className="ml-2 inline-flex shrink-0 rounded-full bg-[var(--tint-purple-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--tint-purple-fg)] dark:bg-indigo-500/15 dark:text-indigo-200">
                  System
                </span>
              </div>
              <ActionOverflowMenu>
                {canUpdate ? (
                  <ActionOverflowMenuItem
                    icon={Pencil}
                    label="Customize for my account"
                    onClick={() => openCustomize(item)}
                  />
                ) : null}
                {canDeleteSystem ? (
                  <ActionOverflowMenuItem
                    icon={Trash2}
                    label="Remove from organization"
                    variant="destructive"
                    onClick={() => setRemoveTarget(item)}
                  />
                ) : null}
              </ActionOverflowMenu>
            </div>
          ))}

          {editableRows.map((item) => (
            <div
              key={item.id}
              className={cn(
                "flex items-center justify-between gap-3 px-4 py-3.5 transition-colors",
                item.id === highlightRoleId &&
                  "bg-violet-500/10 ring-1 ring-inset ring-violet-500/35 dark:bg-violet-500/15"
              )}
            >
              <p className="min-w-0 truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">
                {item.name}
              </p>
              <ActionOverflowMenu>
                {canUpdate ? (
                  <ActionOverflowMenuItem
                    icon={Pencil}
                    label="Edit"
                    onClick={() => openEdit(item)}
                  />
                ) : null}
                {canDelete ? (
                  <ActionOverflowMenuItem
                    icon={Trash2}
                    label="Delete"
                    variant="destructive"
                    onClick={() => setDeleteTarget(item)}
                  />
                ) : null}
              </ActionOverflowMenu>
            </div>
          ))}
        </div>
      </GlassCard>
      </InlineAssistantLayout>

      <DraftLegalRoleSheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setSheetDraft(null);
        }}
        draft={sheetDraft}
        onSaved={() => void loadRoles()}
      />

      <ConfirmDialog
        isOpen={removeTarget != null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveFromOrg}
        title="Remove from organization"
        description={
          removeTarget
            ? `Remove "${removeTarget.name}" from this organization? Other organizations will still see this system default.`
            : ""
        }
        confirmLabel="Remove from organization"
        variant="destructive"
        isLoading={removing}
      />

      <ConfirmDialog
        isOpen={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCustom}
        title="Delete legal role"
        description={
          deleteTarget ? `Delete "${deleteTarget.name}"? This cannot be undone.` : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleting}
      />

    </section>
  );
}
